import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Paper, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ShieldIcon from '@mui/icons-material/Shield';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import UndoIcon from '@mui/icons-material/Undo';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import { getCurrentUser } from '../auth/LoginButton';
import { getUserRoles } from '../rbac/roles';
import {
  checkVerity, dripCadence, dripErrorMessage, dripLeadAction, getDripLeads, markWaitingWelcomedOutside,
  previewWelcomedOutsideBulk, resolveVerityRun, runNightlyCheckNow, verityAddressReasonText, verityControl,
  verityHoldText, verityStopClassText, verityUnfilledText, VERITY_SYSTEM_ACTOR, type DripLead,
  type VerityArmChecks, type VerityHealthResult, type VerityResolveAction, type VerityResolveResult, type VerityRun,
  type VerityRunCheckResult, type VerityScope, type VeritySecretState, type VerityStatus,
  type WelcomedOutsideBulkResult,
} from '../services/drip-api';
// Every time on this panel is a naive UTC string from the server: read it as UTC, shown in local time.
import { formatServerTime as when, parseServerTime } from '../services/server-time';

/**
 * Verity status checks - the panel on the Drip Emails view of the Drip Campaign tab.
 *
 * SIMS reads each EAB lead's status from Verity before it emails them. This is where a person sees
 * what that check is doing and acts on it: Check Verity, arming (unarmed -> dry run -> enforce),
 * the last runs, the review queue the breaker fills, the leads the check held or stopped, and the
 * seven EAB emails switched on together.
 *
 * Roles. Survey.Admin arms, and runs the nightly check now (while armed for dry run). Survey.Admin or
 * Drip.Manager runs Check Verity, disarms, resumes, undoes, releases, merges and switches the cadence on
 * or off. Drip.Manager alone marks the waiting leads welcomed outside SIMS on go-live morning. Buttons a
 * role cannot use are hidden; the server enforces every role whatever this screen shows.
 *
 * Ugly data. Every field is optional and an unknown kind or status renders as text: a list must
 * never take the tab down (the blank-tab lesson).
 *
 * No new personal data. Leads appear here by first name and email, as Verity Leads shows them to the
 * same roles, plus kinds, counts and times. A Verity status name appears only inside a stop reason.
 *
 * Layout tiers (standing rule - separate views, never one fluid rule): the base styles are the
 * laptop, where the sections stack. From 2400px (the 34-inch ultrawide) the sections sit side by
 * side and the counts spread out. Rules inside a media query never reach the laptop.
 */

const ULTRAWIDE = '@media (min-width: 2400px)';

const WRITE_ROLES = ['Survey.Admin', 'Drip.Manager'];
const ADMIN_ROLES = ['Survey.Admin'];
// Admissions' go-live step: "Mark waiting leads as welcomed outside SIMS" (the server's DRIP_WELCOMED_BULK_ROLES).
const WELCOMED_BULK_ROLES = ['Drip.Manager'];
const ARM_CONFIRM: Record<VerityScope, string> = { 'dry-run': 'ARM DRY RUN', enforce: 'ARM ENFORCE' };
const CADENCE_CONFIRM = 'ACTIVATE 7 EMAILS';
const CADENCE_DAYS = [0, 2, 4, 7, 10, 12, 14];
const REASON_MIN = 5;
const REASON_MAX = 300;
const EAB_ID = /^eab-[0-9a-f]{24}$/;
const STALE_RUN_MS = 15 * 60 * 1000;
const MAX_ROWS = 50;

const ARMING_TEXT = 'This is your go-ahead for SIMS to read lead records from Verity. Each read returns '
  + 'the whole record into memory; SIMS keeps only the status and the email flags, and stores neither.';

const RESUME_TEXT = 'SIMS checks Verity again before any email; if Verity still shows a stop status, '
  + 'the lead is stopped again.';

// Run the nightly check now (owner decision 2026-10-28): the confirm dialog's words, as agreed.
const NIGHT_CHECK_TEXT = 'Runs the 2:15 AM check now. It sends no email. Takes up to 3 minutes. It counts '
  + 'toward arming enforce.';
const NIGHT_CHECKS_PER_HOUR = 3;                  // the server's MANUAL_CHECKS_PER_HOUR, everyone together

// The owner's wording (2026-10-07), shown as-is in the bulk "welcomed outside SIMS" confirmation.
const WELCOMED_BULK_TEXT = "Use this once, on go-live morning, right before you activate the emails. These leads "
  + "skip SIMS's welcome and start at the next email. After go-live, SIMS sends the welcome and Admissions stops "
  + 'sending it by hand.';

// What has to be true before the bulk mark, so no lead is marked that nobody welcomed (docs/DRIP-GO-LIVE.md).
const WELCOMED_BULK_CHECKS = [
  'Admissions has sent the first email by hand to every lead that has arrived so far, overnight and weekend '
    + 'leads included. A lead Admissions has not welcomed is marked too, and would then get no welcome at all.',
  'A lead Admissions welcomed on a later day than it arrived: change its date on Verity Leads afterwards, before '
    + "you activate the emails, so SIMS's next email keeps its spacing from the real welcome.",
];

// Why the bulk mark left a lead alone, in plain words (counts only; an unknown reason shows as itself).
const WELCOMED_SKIP_TEXT: Record<string, string> = {
  held: 'on hold', stopped: 'stopped', merged: 'merged', notStarted: 'not started', completed: 'finished',
  otherStatus: 'in another status', emailedBySims: 'already emailed by SIMS', noArrival: 'with no usable arrival date',
  arrivesLater: 'arrived after the count', pastStartWindow: 'past the 30-day start window (SIMS never emails them)',
  changedSince: 'changed while this ran',
};

function welcomedSkipped(skipped?: Record<string, number> | null): string[] {
  return Object.entries(skipped || {}).filter(([, n]) => num(n) > 0)
    .map(([k, n]) => `${num(n)} ${WELCOMED_SKIP_TEXT[k] || k}`);
}

const LAYOUT = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateAreas: '"status" "runs" "review" "holds" "cadence"',
  gap: 2,
  mb: 3,
  [ULTRAWIDE]: {
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)',
    gridTemplateAreas: '"status runs" "cadence runs" "review holds" ". holds"',
    alignItems: 'start',
    gap: 3,
  },
};

const HEAD = {
  color: 'var(--text-primary)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  [ULTRAWIDE]: { px: 2, fontSize: '0.9rem' },
};
// break-word, not anywhere: anywhere also shrinks a column's minimum width to one letter, and the
// table then squeezes the Lead column until every email address breaks mid-word. The tables
// scroll inside their own container, so the page itself never scrolls sideways.
const CELL = {
  color: 'var(--text-secondary)',
  fontSize: '0.8rem',
  overflowWrap: 'break-word',
  verticalAlign: 'top',
  [ULTRAWIDE]: { px: 2, fontSize: '0.875rem' },
};
const NOWRAP = { whiteSpace: 'nowrap', overflowWrap: 'normal' };

// Run counts: a wrapped line on the laptop; on the ultrawide the wider column spaces them out.
const COUNTS = {
  display: 'flex',
  flexWrap: 'wrap',
  columnGap: 1.5,
  rowGap: 0.25,
  minWidth: 0,
  [ULTRAWIDE]: { columnGap: 2.5 },
};

function shortId(id?: string | null): string {
  return id ? String(id).slice(0, 8) : '—';
}

/** The server's own rule: whitespace collapsed, then 5-300 characters. */
function cleanReason(value: string): string {
  return value.split(/\s+/).filter(Boolean).join(' ');
}

function sum(record?: Record<string, number> | null): number {
  return Object.values(record || {}).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** A refusal, in plain sentences. 409s carry lists rather than one message, so those are spelled out. */
function verityErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (data && typeof data === 'object') {
    if (Array.isArray(data.messages) && data.messages.length) return data.messages.join(' ');
    const parts: string[] = [];
    if (Array.isArray(data.missingDays) && data.missingDays.length) {
      parts.push(`No email for day ${data.missingDays.join(', ')}${data.missingDays.includes(0)
        ? ' (day 0 must be the "When a lead is received" trigger)' : ''}.`);
    }
    if (Array.isArray(data.duplicateDays) && data.duplicateDays.length) {
      parts.push(`More than one email for day ${data.duplicateDays.join(', ')}: keep one of each.`);
    }
    if (Array.isArray(data.notRenderable) && data.notRenderable.length) {
      parts.push(`These do not render completely yet: ${data.notRenderable.join(', ')}.`);
    }
    if (Array.isArray(data.alreadyActive) && data.alreadyActive.length) {
      parts.push(`Already active on their own: ${data.alreadyActive.join(', ')}. Pause the cadence first.`);
    }
    if (parts.length) return parts.join(' ');
  }
  return dripErrorMessage(err, fallback);
}

const PAUSE_TEXT: Record<string, string> = {
  'not-armed': 'Verity checks are not armed',
  'control-unreadable': 'the Verity control record could not be read',
  'state-unreadable': 'the Verity state record could not be read',
  'sendgrid-missing': 'SendGrid is not configured',
  'sendgrid-failed': 'SendGrid did not accept an email, so nothing more was sent that run',
  credential_missing: 'the Verity login is missing from Key Vault',
  credential_forbidden: 'SIMS is not allowed to read the Verity login in Key Vault',
  credential_disabled: 'the Verity login is disabled in Key Vault',
  identity_unavailable: 'the SIMS managed identity is unavailable',
  credential_rejected: 'Verity rejected the login',
  auth_failed: 'Verity refused the session token',
  misconfigured: 'Verity answered as if the request were wrong',
  rate_limited: 'Verity asked SIMS to slow down; the next run tries again',
  refused: 'SIMS refused a request it is not allowed to make',
  'consecutive-failures': 'five Verity reads in a row failed',
  unavailable: 'Verity could not be reached',
  timeout: 'Verity did not answer in time',
  deadline: 'the run ran out of time',
};

function pauseText(kind?: string | null): string {
  return (kind && PAUSE_TEXT[kind]) || (kind ? `stopped (${kind})` : 'stopped');
}

function runKindText(kind?: string): string {
  if (kind === 'send') return 'Hourly';
  if (kind === 'sweep') return 'Nightly';
  return kind || '—';
}

function modeText(mode?: string | null): string {
  if (mode === 'enforce') return 'Enforce';
  if (mode === 'dry-run') return 'Dry run';
  if (mode === 'unarmed') return 'Not armed';
  return mode ? String(mode) : 'Unknown';
}

function scopeText(scope?: string | null): string {
  if (scope === 'enforce') return 'enforce';
  if (scope === 'dry-run') return 'the dry run';
  return scope ? String(scope) : 'an unknown scope';
}

function didNotFinish(run: VerityRun): boolean {
  if (run.didNotFinish) return true;
  if (run.status !== 'running') return false;
  // Read as UTC: read as local time, a run on a Pacific browser looked 7 hours younger than it was.
  const started = parseServerTime(run.createdAt);
  return started !== null && Date.now() - started.getTime() > STALE_RUN_MS;
}

function runResult(run: VerityRun): { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' } {
  if (didNotFinish(run)) return { label: 'Did not finish', color: 'warning' };
  switch (run.status) {
    case 'running': return { label: 'Running', color: 'info' };
    case 'ok': return { label: 'OK', color: 'success' };
    case 'budget': return { label: 'Out of time: the rest go next run', color: 'warning' };
    case 'paused': return { label: `Paused: ${pauseText(run.pauseKind)}`, color: 'warning' };
    case 'aborted': return { label: `Ended by an error (${run.pauseKind || 'unknown'})`, color: 'error' };
    default: return { label: run.status ? String(run.status) : 'Unknown', color: 'default' };
  }
}

function stopTotal(run: VerityRun): number {
  return sum(run.counts?.stop);
}

/** A run's stops by class (stop | never | dne). A class this build does not know shows as itself. */
function stopItems(stop: Record<string, number> | undefined, would: boolean): string[] {
  return Object.entries(stop || {}).filter(([, n]) => num(n))
    .map(([cls, n]) => `${num(n)} ${would ? 'would stop' : 'stopped'} (${cls === 'dne' ? 'Do Not Email' : cls})`);
}

/**
 * A run's holds by kind, address-not-usable split by why, and unfilled-placeholder by which placeholder
 * ("1 held (no first name on file)"). A kind or code this build does not know shows as itself.
 */
function holdItems(hold: Record<string, number> | undefined, addressReasons: Record<string, number> | undefined,
                   would: boolean, unfilledTokens?: Record<string, number>): string[] {
  const items: string[] = [];
  for (const [kind, n] of Object.entries(hold || {})) {
    if (!num(n)) continue;
    const verb = would ? 'would hold' : 'held';
    let rest = num(n);
    if (kind === 'address-not-usable') {
      // Split by why: the run's counts, the only place a reason is kept. Runs from before the reasons
      // were counted have none, and read as before.
      for (const [reason, m] of Object.entries(addressReasons || {})) {
        if (!num(m)) continue;
        items.push(`${num(m)} ${verb} (${verityHoldText(kind).short}: ${verityAddressReasonText(reason) ?? reason})`);
        rest -= num(m);
      }
    }
    if (kind === 'unfilled-placeholder') {
      // Named by placeholder, one count per person and name: a person whose email lacked two counts
      // under each, so the counts are shown only when there is more than one name.
      const names = Object.entries(unfilledTokens || {}).filter(([, m]) => num(m));
      if (names.length) {
        const words = names.map(([name, m]) => verityUnfilledText(name) + (names.length > 1 ? ` ${num(m)}` : ''));
        items.push(`${rest} ${verb} (${words.join(', ')})`);
        continue;
      }
    }
    if (rest > 0) items.push(`${rest} ${verb} (${verityHoldText(kind).short})`);
  }
  return items;
}

function errorItem(errors: Record<string, number> | undefined): string | null {
  const total = sum(errors);
  if (!total) return null;
  const detail = Object.entries(errors || {}).filter(([, n]) => num(n)).map(([k, n]) => `${k} ${n}`).join(', ');
  return `${total} errors (${detail})`;
}

/** Every count worth showing, in reading order. A dry run's counts are what SIMS WOULD have done. */
function countItems(run: VerityRun): string[] {
  const c = run.counts || {};
  const would = run.mode !== 'enforce';
  const items = [`${num(c.evaluated)} checked`];
  items.push(would ? `${num(c.allow)} would be emailed` : `${num(c.allow)} allowed`);
  if (!would) items.push(`${num(c.sent)} sent`);
  if (num(c.sendFailed)) items.push(`${num(c.sendFailed)} failed to send`);
  items.push(...stopItems(c.stop, would));
  items.push(...holdItems(c.hold, c.addressReasons, would, c.unfilledTokens));
  if (num(c.cleared)) items.push(`${num(c.cleared)} holds cleared`);
  if (num(c.day0Starts)) items.push(`${num(c.day0Starts)} Day-0 ${would ? 'would start' : 'started'}`);
  if (num(c.welcomedOutsideStarts)) {
    items.push(`${num(c.welcomedOutsideStarts)} ${would ? 'would start' : 'started'} after a welcome sent outside SIMS`);
  }
  if (num(c.notStarted)) items.push(`${num(c.notStarted)} ${would ? 'would be ' : ''}not started`);
  if (num(c.notTargeted)) items.push(`${num(c.notTargeted)} not in the step's lead statuses (not sent)`);
  if (num(c.unknownStatus)) items.push(`${num(c.unknownStatus)} with status Unknown`);
  if (num(c.notReached)) items.push(`${num(c.notReached)} not reached`);
  const errors = errorItem(c.errors);
  if (errors) items.push(errors);
  if (run.breaker?.tripped) items.push(would ? 'safety limit would trip' : 'safety limit tripped');
  return items;
}

// ---------------------------------------------------------------------------------------------
// Run the nightly check now
// ---------------------------------------------------------------------------------------------

/** What the check counted, in reading order: always a dry run, so "would" counts. */
function nightCheckItems(r: VerityRunCheckResult): string[] {
  const c = r.counts || {};
  const items = [`${num(c.persons)} ${num(c.persons) === 1 ? 'person' : 'people'} to check`,
    `${num(c.lookups)} Verity ${num(c.lookups) === 1 ? 'read' : 'reads'}`,
    `${num(c.allow)} would be emailed${num(c.vouched) ? ` (${num(c.vouched)} on EAB's address: Verity listed none)` : ''}`];
  items.push(...stopItems(c.stop, true));
  items.push(...holdItems(c.hold, c.addressReasons, true));
  if (num(c.notReached)) items.push(`${num(c.notReached)} not reached`);
  const errors = errorItem(c.errors);
  if (errors) items.push(errors);
  return items;
}

/** Why a check is not the dry-run night enforce needs, in the gate's own terms (ARM_CHECK_TEXT). */
function nightNotCounted(r: VerityRunCheckResult): string {
  if (r.status !== 'ok') {
    return r.status === 'budget' ? 'it ran out of time before every lead was checked'
      : `it did not finish OK (${runResult({ id: r.runId, status: r.status, pauseKind: r.pauseKind }).label})`;
  }
  if (!r.provesReads) return 'it read no lead from Verity, or a read came back malformed';
  if (!r.wouldEmail) {
    // Name only what the counts below show: a lead can also be left undecided (not read, or not one SIMS emails).
    const stopped = sum(r.counts?.stop) > 0;
    const held = sum(r.counts?.hold) > 0;
    const why = stopped && held ? 'the stops and holds below say why' : stopped ? 'the stops below say why'
      : held ? 'the holds below say why' : '';
    return `it found no lead SIMS would email${why ? `; ${why}` : ''}`;
  }
  // The gate reads the saved record, and this run's last write failed: it ran as the night, but cannot count.
  if (r.recordSaved === false) {
    return 'SIMS could not save its result, and arming enforce counts only a saved one (Last runs will show '
      + 'this run as Did not finish). Run the check again';
  }
  return 'it does not meet the conditions under Before arming';
}

/** A refusal or a lost answer, in plain words. */
function nightErrorMessage(err: any): string {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 429) {
    const next = typeof data?.nextAllowedAt === 'string' ? ` The next is allowed at ${when(data.nextAllowedAt)}.` : '';
    return `${dripErrorMessage(err, `Run the nightly check now is limited to ${NIGHT_CHECKS_PER_HOUR} an hour.`)}${next}`;
  }
  if (!err?.response || status === 502 || status === 504) {
    return 'No answer came back in time. The check may still be running: look under Last runs in a few minutes '
      + 'before you press again.';
  }
  return dripErrorMessage(err, 'The check could not be run.');
}

/**
 * armedForEnforce: the checks are already armed for enforce, and only SIMS_DISABLE_DRIP_SENDS keeps them
 * rehearsing (the server's run_mode), so there is nothing left to arm.
 */
function NightCheckResult({ result, otherUnmet, armedForEnforce }: {
  result: VerityRunCheckResult; otherUnmet: string[]; armedForEnforce: boolean;
}) {
  const finished = runResult({ id: result.runId, status: result.status, pauseKind: result.pauseKind });
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {result.dryRunNightOk
        ? <Alert severity="success">This check counts toward arming enforce: it read leads from Verity, finished OK and found at least one lead SIMS would email. No email was sent.</Alert>
        : <Alert severity="warning">This check does not count toward arming enforce: {nightNotCounted(result)}. No email was sent.</Alert>}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip size="small" label={finished.label} color={finished.color}
              sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }} />
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
          Run {shortId(result.runId)}, under Last runs as started by you.
        </Typography>
      </Box>
      <Box sx={{ ...COUNTS, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
        {nightCheckItems(result).map((item) => <span key={item}>{item}</span>)}
      </Box>
      {result.dryRunNightOk && (armedForEnforce
        ? (
          <Alert severity="info">
            Already armed for enforce: SIMS_DISABLE_DRIP_SENDS is on, so SIMS keeps rehearsing until it is
            switched off. There is nothing to arm again.
          </Alert>
        )
        : otherUnmet.length === 0
          ? <Alert severity="info">Enforce can be armed now: <strong>Arm enforce</strong>, and type ARM ENFORCE.</Alert>
          : (
            <Alert severity="info">
              Before arming enforce:
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {otherUnmet.map((t) => <li key={t}>{t}</li>)}
              </Box>
            </Alert>
          ))}
    </Box>
  );
}

function leadLabel(lead: DripLead | undefined, fallback: { id: string; leadId?: string }): string {
  if (!lead) return fallback.leadId || shortId(fallback.id);
  return lead.firstName ? `${lead.firstName} · ${lead.email}` : (lead.email || lead.leadId || shortId(lead.id));
}

function normalise(email?: string): string {
  return (email || '').trim().toLowerCase();
}

/** Every eab- id of the person: each record's leadId and verityExternalIds, minus retiredIds. */
function personVerityIds(person: DripLead[]): string[] {
  const ids = new Set<string>();
  const retired = new Set<string>();
  for (const r of person) {
    if (r.status === 'merged') continue;
    if (EAB_ID.test(r.leadId || '')) ids.add(r.leadId);
    for (const i of r.verityExternalIds || []) if (EAB_ID.test(i)) ids.add(i);
    for (const i of r.retiredIds || []) retired.add(i);
  }
  return [...ids].filter((i) => !retired.has(i)).sort();
}

// ---------------------------------------------------------------------------------------------
// Dialog building blocks
// ---------------------------------------------------------------------------------------------

/**
 * A confirm dialog that asks for a reason (and, where it matters, a typed confirmation). Every
 * action here is audited with the reason and the person's name, so the reason is never optional.
 * `onConfirm` throws to show the server's refusal in place.
 */
function ActionDialog({
  open, title, children, typeToConfirm, confirmLabel, danger, blocked, onConfirm, onClose,
}: {
  open: boolean;
  title: string;
  children?: ReactNode;
  typeToConfirm?: string;
  confirmLabel: string;
  danger?: boolean;
  blocked?: boolean;
  onConfirm: (reason: string, typed: string) => Promise<void>;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setReason(''); setTyped(''); setError(null); setBusy(false); }
  }, [open]);

  const clean = cleanReason(reason);
  const reasonOk = clean.length >= REASON_MIN && clean.length <= REASON_MAX;
  const typedOk = !typeToConfirm || typed.trim() === typeToConfirm;

  const go = async () => {
    setBusy(true); setError(null);
    try {
      await onConfirm(clean, typed.trim());
    } catch (err) {
      setError(verityErrorMessage(err, 'That could not be completed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {children}
        {typeToConfirm && (
          <TextField size="small" label={`Type ${typeToConfirm} to confirm`} value={typed}
                     onChange={(e) => setTyped(e.target.value)} disabled={busy}
                     inputProps={{ 'aria-label': `Type ${typeToConfirm} to confirm`, autoComplete: 'off' }} />
        )}
        <TextField size="small" label="Reason" value={reason} multiline minRows={2}
                   onChange={(e) => setReason(e.target.value)} disabled={busy}
                   error={reason.length > 0 && !reasonOk}
                   helperText={`${REASON_MIN} to ${REASON_MAX} characters. Recorded with your name.`}
                   inputProps={{ 'aria-label': 'Reason', maxLength: REASON_MAX + 50 }} />
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color={danger ? 'error' : 'primary'}
                disabled={busy || blocked || !reasonOk || !typedOk} onClick={() => void go()}>
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Section({ title, area, action, children }: {
  title: string; area: string; action?: ReactNode; children: ReactNode;
}) {
  return (
    <Paper sx={{
      p: 2, gridArea: area, minWidth: 0, backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--card-border)', [ULTRAWIDE]: { p: 3 },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography component="h3" sx={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1.05rem', minWidth: 0 }}>
          {title}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {action}
      </Box>
      {children}
    </Paper>
  );
}

function Line({ children, muted }: { children: ReactNode; muted?: boolean }) {
  return (
    <Typography variant="body2" sx={{ color: muted ? 'var(--text-secondary)' : 'var(--text-primary)', overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  );
}

// ---------------------------------------------------------------------------------------------
// Check Verity
// ---------------------------------------------------------------------------------------------

function secretSentence(label: 'username' | 'password', s?: VeritySecretState): { ok: boolean; text: string } {
  const name = `sims-verity-api-${label}`;
  switch (s?.state) {
    case 'present':
      return { ok: true, text: `The Verity ${label} is in Key Vault${s.versionTag ? ` (version ${s.versionTag})` : ''}.` };
    case 'missing':
      return { ok: false, text: `The Verity ${label} is not in Key Vault yet (secret ${name} in laurus-eab-kv).` };
    case 'forbidden':
      return { ok: false, text: `SIMS is not allowed to read the Verity ${label} (${name}). The role grant is missing, or has not reached SIMS yet: allow about 10 minutes.` };
    case 'disabled':
      return { ok: false, text: `The Verity ${label} is disabled in Key Vault (${name}).` };
    case 'identity_unavailable':
      return { ok: false, text: `SIMS's managed identity could not sign in to Key Vault, so the ${label} was not read.` };
    case 'unavailable': case 'timeout': case 'deadline':
      return { ok: false, text: `Key Vault did not answer for the ${label}. Try again shortly.` };
    default:
      return { ok: false, text: `The Verity ${label} could not be read (${s?.state || 'no answer'}).` };
  }
}

function tokenSentence(t?: VerityHealthResult['token']): { ok: boolean; text: string } {
  switch (t?.state) {
    case 'ok':
      return { ok: true, text: `Verity accepted the login${typeof t.ms === 'number' ? ` (${t.ms} ms)` : ''}.` };
    case 'latched':
      return { ok: false, text: 'Not tried: Verity rejected this login earlier, and SIMS will not try it again until a new secret version exists. Fix the value in Key Vault, add a new version, then Check Verity again.' };
    case 'credential_rejected':
      return { ok: false, text: 'Verity rejected the login. SIMS will not try it again until a new secret version exists, so it cannot lock the account. Fix the value in Key Vault, add a new version, then Check Verity again.' };
    case 'skipped':
      return { ok: false, text: 'Not tried, because the login could not be read from Key Vault.' };
    case 'rate_limited':
      return { ok: false, text: 'Verity asked SIMS to slow down. Try again later.' };
    case 'misconfigured':
      return { ok: false, text: 'Verity answered as if the request were wrong (method or route). The login was not marked as rejected.' };
    case 'unavailable': case 'timeout': case 'deadline':
      return { ok: false, text: 'Verity could not be reached or did not answer in time. Try again shortly.' };
    default:
      return { ok: false, text: `The login could not be checked (${t?.state || 'no answer'}).` };
  }
}

function NameChips({ label, names, color }: { label: string; names?: string[]; color: 'warning' | 'error' | 'default' }) {
  if (!names || names.length === 0) return null;
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {names.map((n) => <Chip key={n} size="small" label={n} color={color} variant="outlined" />)}
      </Box>
    </Box>
  );
}

function HealthResult({ result }: { result: VerityHealthResult }) {
  const user = secretSentence('username', result.credential?.username);
  const pass = secretSentence('password', result.credential?.password);
  const token = tokenSentence(result.token);
  const list = result.statusList;
  const listOk = list?.state === 'ok';
  const listText = listOk
    ? `Verity lists ${num(list?.count)} lead statuses; ${num(list?.matched)} match SIMS's catalogue.`
    : list?.state === 'skipped'
      ? 'The status list was not read, because the login was not accepted.'
      : `The status list could not be read (${list?.state || 'no answer'}).`;
  const rows: [boolean, string][] = [[user.ok, user.text], [pass.ok, pass.text], [token.ok, token.text], [listOk, listText]];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {result.ok
        ? <Alert severity="success">Everything checked out. Arming is allowed for the next 60 minutes. No lead data was read.</Alert>
        : <Alert severity="warning">Not ready to arm yet. Fix what is marked below, then Check Verity again. No lead data was read.</Alert>}
      {rows.map(([ok, text]) => (
        <Box key={text} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          {ok ? <CheckCircleIcon fontSize="small" color="success" sx={{ mt: 0.25 }} />
            : <RadioButtonUncheckedIcon fontSize="small" color="warning" sx={{ mt: 0.25 }} />}
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{text}</Typography>
        </Box>
      ))}
      <NameChips label="Verity names SIMS does not know (they hold the lead, and block enforce)"
                 names={list?.unmapped} color="warning" />
      <NameChips label="Of those, a known status under a different category than SIMS's catalogue"
                 names={list?.categoryMismatch} color="warning" />
      <NameChips label="Listed by Verity more than once (blocks enforce)" names={list?.duplicateNames} color="error" />
      <NameChips label="In SIMS's catalogue but not listed by Verity" names={list?.missing} color="default" />
      {result.checkedAt && (
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>Checked {when(result.checkedAt)}.</Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------------------------
// Arm checks
// ---------------------------------------------------------------------------------------------

type CheckKey = keyof VerityArmChecks;

function armCheckRows(checks: VerityArmChecks | null | undefined, hasCatalogue: boolean): { key: CheckKey; met: boolean; text: string; scope: string }[] {
  const c = checks || {};
  return [
    { key: 'healthFresh', met: !!c.healthFresh, scope: 'to arm',
      text: c.healthFresh ? 'Check Verity passed less than 60 minutes ago.'
        : 'Run Check Verity first: arming needs a successful check less than 60 minutes old.' },
    { key: 'noUnmapped', met: !!c.noUnmapped, scope: 'for enforce',
      text: c.noUnmapped ? "Every Verity status name is in SIMS's catalogue."
        : hasCatalogue ? 'Verity has status names SIMS does not know. Update the status catalogue before enforcing.'
          : "Run Check Verity to compare Verity's status names with SIMS's catalogue." },
    { key: 'noDuplicateNames', met: !!c.noDuplicateNames, scope: 'for enforce',
      text: c.noDuplicateNames ? 'Verity lists each status name once.'
        : hasCatalogue ? 'Verity lists the same status name twice. Resolve it before enforcing.'
          : 'Run Check Verity to see whether Verity lists any status name twice.' },
    // The same words as the server's refusal (utils/drip_verity ARM_CHECK_TEXT).
    { key: 'dryRunNightOk', met: !!c.dryRunNightOk, scope: 'for enforce',
      text: c.dryRunNightOk
        ? 'A dry-run night in the last 7 days read leads from Verity, finished cleanly and found at least one lead SIMS would email.'
        : 'Enforce needs a successful dry-run night within the last 7 days: the nightly check in dry run, finished OK, with at least one lead read from Verity, none malformed, and at least one lead SIMS would email. A night that would email nobody does not count: its counts under Last runs say why the leads were held.' },
  ];
}

function unmetFor(scope: VerityScope, checks: VerityArmChecks | null | undefined, hasCatalogue: boolean): string[] {
  const needed: CheckKey[] = scope === 'dry-run'
    ? ['healthFresh'] : ['healthFresh', 'noUnmapped', 'noDuplicateNames', 'dryRunNightOk'];
  return armCheckRows(checks, hasCatalogue).filter((r) => needed.includes(r.key) && !r.met).map((r) => r.text);
}

// ---------------------------------------------------------------------------------------------
// Run actions: preview, undo, apply, release
// ---------------------------------------------------------------------------------------------

interface RunTarget {
  runId: string;
  family: 'undo' | 'review';
  action: VerityResolveAction;
  /** "the Hourly run of 6 Oct 2026, 08:00" */
  label: string;
}

const RUN_ACTION_TEXT: Record<VerityResolveAction, { title: string; confirm?: string; body: string }> = {
  'preview-undo': {
    title: 'Stops made by this run',
    body: 'These leads were stopped by this run’s Verity check. Undo makes them active again. ' + RESUME_TEXT,
  },
  'undo-stops': {
    title: 'Undo these stops?',
    confirm: 'Undo these stops',
    body: 'Each lead below becomes active again. ' + RESUME_TEXT,
  },
  'preview-pending': {
    title: 'Leads held for review',
    body: 'This run would have stopped more leads than the safety limit allows, so it held them for a person to decide. Nobody below has been emailed or stopped.',
  },
  'apply-pending': {
    title: 'Apply these stops?',
    confirm: 'Apply stops',
    body: 'Each lead below is stopped, as Verity’s status says. A stop can be resumed later, one lead at a time.',
  },
  'release-pending': {
    title: 'Release these leads?',
    confirm: 'Release',
    body: 'Each lead below goes back to active and is checked against Verity again at the next run. Nobody is emailed without that check.',
  },
};

function resolveSummary(r: VerityResolveResult): string {
  const c = r.counts || {};
  const parts = [`${num(c.done)} done`];
  if (num(c.changedSince)) parts.push(`${num(c.changedSince)} had changed since and were left alone`);
  if (num(c.skipped)) parts.push(`${num(c.skipped)} skipped`);
  if (num(c.remaining)) parts.push(`${num(c.remaining)} not reached`);
  return `${parts.join(', ')}.${r.incomplete ? ' It ran out of time part-way; it is safe to run it again.' : ''}`;
}

function RunDialog({ target, leadsById, onClose, onDone }: {
  target: RunTarget | null;
  leadsById: Map<string, DripLead>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [preview, setPreview] = useState<VerityResolveResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    let live = true;
    setPreview(null); setError(null); setReason(''); setBusy(false); setResult(null); setLoading(true);
    const previewAction: VerityResolveAction = target.family === 'undo' ? 'preview-undo' : 'preview-pending';
    resolveVerityRun(target.runId, { action: previewAction, confirm: target.runId })
      .then((r) => { if (live) setPreview(r); })
      .catch((err) => { if (live) setError(verityErrorMessage(err, 'The leads for this run could not be listed.')); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [target]);

  if (!target) return null;
  const text = RUN_ACTION_TEXT[target.action];
  const writes = !!text.confirm;
  const leads = preview?.leads || [];
  const skipped = preview?.skipped || [];
  const clean = cleanReason(reason);
  const reasonOk = clean.length >= REASON_MIN && clean.length <= REASON_MAX;

  const go = async () => {
    setBusy(true); setError(null);
    try {
      const r = await resolveVerityRun(target.runId, { action: target.action, confirm: target.runId, reason: clean });
      const summary = resolveSummary(r);
      setResult(summary);
      onDone(`${text.confirm}: ${summary}`);
    } catch (err) {
      setError(verityErrorMessage(err, 'That could not be completed; it is safe to run it again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{text.title}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2">{text.body}</Typography>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
          From {target.label} (run {shortId(target.runId)}).
        </Typography>
        {loading && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>}
        {preview && (
          leads.length === 0
            ? <Alert severity="info">No lead is waiting on this. Nothing would change.</Alert>
            : (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {leads.map((l) => {
                  const lead = leadsById.get(l.id);
                  const stopReason = target.family === 'review' ? lead?.verityHold?.stopReason : null;
                  return (
                    <li key={l.id}>
                      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                        {leadLabel(lead, l)}{stopReason ? ` — would stop: ${stopReason}` : ''}
                      </Typography>
                    </li>
                  );
                })}
              </Box>
            )
        )}
        {skipped.length > 0 && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Not included</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {skipped.map((s) => (
                <li key={s.id}>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {leadLabel(leadsById.get(s.id), s)}: {s.reason || 'changed since'}
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
        )}
        {writes && !result && (
          <TextField size="small" label="Reason" value={reason} multiline minRows={2}
                     onChange={(e) => setReason(e.target.value)} disabled={busy}
                     error={reason.length > 0 && !reasonOk}
                     helperText={`${REASON_MIN} to ${REASON_MAX} characters. Recorded with your name.`}
                     inputProps={{ 'aria-label': 'Reason' }} />
        )}
        {result && <Alert severity="success">{result}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{writes && !result ? 'Cancel' : 'Close'}</Button>
        {writes && !result && (
          <Button variant="contained" color={target.action === 'release-pending' ? 'primary' : 'error'}
                  disabled={busy || loading || !preview || leads.length === 0 || !reasonOk}
                  onClick={() => void go()}>
            {busy ? 'Working…' : text.confirm}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Holds and stops
// ---------------------------------------------------------------------------------------------

type Group = 'review' | 'held' | 'verity-stop' | 'would' | 'not-started' | 'duplicate';

const GROUP_LABEL: Record<Group, string> = {
  review: 'Waiting for review',
  held: 'On hold',
  'verity-stop': 'Stopped by Verity',
  would: 'Would stop or hold (dry run)',
  'not-started': 'Not started',
  duplicate: 'Duplicate records',
};

const GROUP_COLOR: Record<Group, 'default' | 'warning' | 'error' | 'info'> = {
  review: 'warning', held: 'warning', 'verity-stop': 'error', would: 'info', 'not-started': 'default', duplicate: 'warning',
};

interface HoldRow {
  lead: DripLead;
  group: Group;
  what: string;
  since?: string | null;
  runId?: string | null;
}

function holdRows(leads: DripLead[], mode?: string): HoldRow[] {
  const rows: HoldRow[] = [];
  for (const lead of leads) {
    if (!lead || lead.status === 'merged') continue;
    const records = lead.personRecords ?? 0;
    if (lead.status === 'held') {
      const hold = lead.verityHold || {};
      const review = hold.kind === 'stop-pending-review';
      rows.push({
        lead, group: review ? 'review' : 'held',
        what: verityHoldText(hold.kind).long + (review && hold.stopReason ? ` It would stop: ${hold.stopReason}.` : ''),
        since: hold.since, runId: hold.runId,
      });
    } else if (lead.status === 'stopped' && lead.stoppedBy === VERITY_SYSTEM_ACTOR) {
      rows.push({
        lead, group: 'verity-stop', what: lead.stoppedReason || 'Stopped by the Verity check.',
        since: lead.stoppedAt, runId: lead.stoppedRunId,
      });
    } else if (lead.status === 'not_started') {
      rows.push({
        lead, group: 'not-started',
        what: 'Arrived more than 30 days before it could first be emailed, so the sequence was not started. Never emailed.',
        since: lead.notStartedAt,
      });
    } else if (mode !== 'enforce' && lead.status === 'active' && lead.verityShadow
               && (lead.verityShadow.action === 'stop' || lead.verityShadow.action === 'hold')) {
      const s = lead.verityShadow;
      rows.push({
        lead, group: 'would',
        what: s.action === 'stop'
          ? `Would stop: ${verityStopClassText(s.kind)}.`
          : `Would hold: ${verityHoldText(s.kind).long}`,
        since: s.since, runId: s.runId,
      });
    } else if (records > 1 && lead.status !== 'completed') {
      rows.push({
        lead, group: 'duplicate',
        what: `${records} SIMS records share this address. Merge them so the person is emailed once.`,
      });
    }
  }
  return rows.sort((a, b) => String(b.since || '').localeCompare(String(a.since || '')));
}

type LeadPending = { action: 'resume' | 'release-hold' | 'merge-duplicates'; lead: DripLead } | null;

/** The hold a Release would act on. In a dry run a possible-duplicate hold exists only as a shadow,
 *  and it can be released before enforce ever writes it (the hand-offs so far are accepted). */
function releasableKind(lead: DripLead): string | null {
  const kind = lead.verityHold?.kind;
  if (lead.status === 'held' && (kind === 'possible-duplicate-verity-record' || kind === 'not-found')) return kind;
  if (lead.status === 'active' && lead.possibleVerityDuplicate) return 'possible-duplicate-verity-record';
  return null;
}
type Pending =
  | { type: 'arm'; scope: VerityScope }
  | { type: 'disarm' }
  | { type: 'cadence'; action: 'activate' | 'pause' }
  | null;

// ---------------------------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------------------------

export default function VerityChecksPanel({ status, statusError, loading, refreshKey, onChanged }: {
  status: VerityStatus | null;
  statusError: string | null;
  loading: boolean;
  /** 0 until the tab's first load finishes, then bumped on every reload, so the lead list reloads with it. */
  refreshKey: number;
  onChanged: () => void;
}) {
  const roles = getUserRoles(getCurrentUser());
  const canWrite = roles.some((r) => WRITE_ROLES.includes(r));
  const canArm = roles.some((r) => ADMIN_ROLES.includes(r));
  const canMarkWaiting = roles.some((r) => WELCOMED_BULK_ROLES.includes(r));

  const [leads, setLeads] = useState<DripLead[]>([]);
  const [leadsError, setLeadsError] = useState<string | null>(null);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState<Group | 'all'>('all');

  const [healthOpen, setHealthOpen] = useState(false);
  const [healthBusy, setHealthBusy] = useState(false);
  const [health, setHealth] = useState<VerityHealthResult | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);

  const [pending, setPending] = useState<Pending>(null);
  const [leadPending, setLeadPending] = useState<LeadPending>(null);
  const [retire, setRetire] = useState<string[]>([]);
  const [runTarget, setRunTarget] = useState<RunTarget | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState<WelcomedOutsideBulkResult | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [nightOpen, setNightOpen] = useState(false);
  const [nightBusy, setNightBusy] = useState(false);
  const [nightResult, setNightResult] = useState<VerityRunCheckResult | null>(null);
  const [nightError, setNightError] = useState<string | null>(null);

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const res = await getDripLeads();
      setLeads(Array.isArray(res?.leads) ? res.leads : []);
      setLeadsError(null);
    } catch (err) {
      setLeadsError(dripErrorMessage(err, 'The leads could not be loaded.'));
    } finally {
      setLeadsLoading(false);
    }
  }, []);

  useEffect(() => { if (canWrite && refreshKey > 0) void loadLeads(); }, [canWrite, loadLeads, refreshKey]);

  const leadsById = useMemo(() => new Map(leads.filter(Boolean).map((l) => [l.id, l])), [leads]);
  const rows = useMemo(() => holdRows(leads, status?.mode), [leads, status?.mode]);
  const groupCounts = useMemo(() => {
    const out: Partial<Record<Group, number>> = {};
    for (const r of rows) out[r.group] = (out[r.group] || 0) + 1;
    return out;
  }, [rows]);
  const shownRows = filter === 'all' ? rows : rows.filter((r) => r.group === filter);

  const done = (message: string) => {
    setToast(message);
    onChanged();
  };

  if (!canWrite) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        The Verity status checks are for Survey.Admin and Drip.Manager.
      </Alert>
    );
  }
  if (!status) {
    if (statusError) {
      return <Alert severity="error" sx={{ mb: 3 }}>The Verity status checks could not be loaded: {statusError}</Alert>;
    }
    return loading ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={26} /></Box> : null;
  }

  const runs = Array.isArray(status.runs) ? status.runs.filter(Boolean) : [];
  const runsById = new Map(runs.map((r) => [r.id, r]));
  const describeRun = (runId: string): string => {
    const run = runsById.get(runId);
    return run ? `the ${runKindText(run.kind)} run of ${when(run.createdAt)}` : `run ${shortId(runId)}`;
  };
  // The server's rule (run_mode): an arm without a known scope is not armed.
  const arm = status.arm && typeof status.arm === 'object'
    && (status.arm.scope === 'dry-run' || status.arm.scope === 'enforce') ? status.arm : null;
  const hasCatalogue = !!status.catalogue;
  const checks = armCheckRows(status.armChecks, hasCatalogue);
  const history = Array.isArray(status.history) ? status.history.filter(Boolean).slice(0, 5) : [];
  const pendingReview = Array.isArray(status.pendingReview) ? status.pendingReview.filter((p) => p && p.runId) : [];
  const cadence = status.cadence && typeof status.cadence === 'object' ? status.cadence : null;
  const required = num(cadence?.required) || CADENCE_DAYS.length;
  const activeCount = num(cadence?.active);
  // SIMS can send right now, so go-live has happened: the server refuses the bulk mark (it also refuses
  // once SIMS has emailed any lead, which the dialog's count reports).
  const simsCanSend = status.mode === 'enforce' && activeCount > 0;

  const modeChip = (() => {
    if (status.mode === 'enforce') return <Chip label="Enforcing" color="success" />;
    if (status.mode === 'dry-run') {
      return <Chip label={arm?.scope === 'enforce' ? 'Armed for enforce · rehearsing' : 'Dry run'} color="info" />;
    }
    if (status.mode === 'unarmed') return <Chip label="Not armed" variant="outlined" />;
    return <Chip label={`Mode: ${status.mode ?? 'unknown'}`} color="warning" variant="outlined" />;
  })();

  // Go-live morning: the count comes from the server's own filter (a preview writes nothing), so the
  // number in the dialog is the number the confirmed run marks.
  const openBulk = async () => {
    setBulkOpen(true); setBulkBusy(true); setBulkPreview(null); setBulkError(null);
    try {
      setBulkPreview(await previewWelcomedOutsideBulk());
    } catch (err) {
      setBulkError(verityErrorMessage(err, 'The waiting leads could not be counted.'));
    } finally {
      setBulkBusy(false);
    }
  };

  // arrivedBefore = the preview's asOf: a lead arriving while the dialog is open is left for SIMS's own welcome.
  const runBulk = async () => {
    setBulkBusy(true); setBulkError(null);
    try {
      const r = await markWaitingWelcomedOutside(bulkPreview?.asOf || undefined);
      setBulkOpen(false);
      done(`Marked ${num(r.marked)} ${num(r.marked) === 1 ? 'lead' : 'leads'} as welcomed outside SIMS`
        + (num(r.alreadyMarked) ? ` (${num(r.alreadyMarked)} already were)` : '')
        + (r.incomplete || num(r.failed)
          ? '. Some were not reached: run it again now, before you activate the emails.' : '.'));
    } catch (err) {
      setBulkError(verityErrorMessage(err, 'The leads could not be marked; it is safe to try again.'));
    } finally {
      setBulkBusy(false);
    }
  };

  const runCheck = async () => {
    setHealthOpen(true); setHealthBusy(true); setHealth(null); setHealthError(null);
    try {
      setHealth(await checkVerity());
      onChanged();
    } catch (err) {
      setHealthError(verityErrorMessage(err, 'Check Verity could not complete.'));
    } finally {
      setHealthBusy(false);
    }
  };

  // Run the nightly check now: the server's rule (run_mode 'dry-run') - armed for dry run, or armed for
  // enforce while SIMS_DISABLE_DRIP_SENDS is on. The request waits for the check (up to 3 minutes).
  const nightAllowed = status.mode === 'dry-run';
  const nightTip = status.mode === 'enforce'
    ? 'Enforcing: SIMS checks the leads itself, every hour and at 2:15 AM Pacific.'
    : !nightAllowed ? 'Arm the dry run first: the check runs now only in a dry run.'
      : `Runs the 2:15 AM Pacific check now, in dry run. At most ${NIGHT_CHECKS_PER_HOUR} an hour, for everyone together.`;
  const openNight = () => { setNightOpen(true); setNightResult(null); setNightError(null); };
  const runNight = async () => {
    setNightBusy(true); setNightError(null);
    try {
      setNightResult(await runNightlyCheckNow());
    } catch (err) {
      setNightError(nightErrorMessage(err));
    } finally {
      setNightBusy(false);
      onChanged();          // the status and Last runs - also when the answer was lost but the check ran
    }
  };
  // With this check counted, what else arming enforce needs (the status reloads behind the dialog).
  const nightOtherUnmet = armCheckRows(status.armChecks, hasCatalogue)
    .filter((r) => r.key !== 'dryRunNightOk' && !r.met).map((r) => r.text);

  // ── dialogs ────────────────────────────────────────────────────────────────────────────────
  const armScope = pending?.type === 'arm' ? pending.scope : null;
  const armUnmet = armScope ? unmetFor(armScope, status.armChecks, hasCatalogue) : [];

  const leadPerson = leadPending
    ? leads.filter((l) => l && l.status !== 'merged' && normalise(l.email) === normalise(leadPending.lead.email))
    : [];
  const personIds = personVerityIds(leadPerson.length ? leadPerson : leadPending ? [leadPending.lead] : []);
  const notFoundIds = leadPending?.lead.verityHold?.notFoundIds || [];
  const releaseKind = leadPending?.action === 'release-hold' ? releasableKind(leadPending.lead) : null;
  const retiresAll = personIds.length > 0 && personIds.every((i) => retire.includes(i));
  const survivor = [...leadPerson].sort((a, b) =>
    `${a.createdAt || ''}|${a.id}`.localeCompare(`${b.createdAt || ''}|${b.id}`))[0];

  const openLead = (action: 'resume' | 'release-hold' | 'merge-duplicates', lead: DripLead) => {
    setRetire([]);
    setLeadPending({ action, lead });
  };

  const rowActions = (row: HoldRow) => {
    const l = row.lead;
    const buttons: ReactNode[] = [];
    if (l.status === 'stopped' && l.stoppedBy === VERITY_SYSTEM_ACTOR) {
      buttons.push(
        <Button key="resume" size="small" startIcon={<ReplayIcon />} onClick={() => openLead('resume', l)}>Resume</Button>);
    }
    if (releasableKind(l)) {
      buttons.push(
        <Button key="release" size="small" startIcon={<LockOpenIcon />} onClick={() => openLead('release-hold', l)}>
          Release hold
        </Button>);
    }
    if ((l.personRecords ?? 0) > 1) {
      buttons.push(
        <Button key="merge" size="small" startIcon={<CallMergeIcon />} onClick={() => openLead('merge-duplicates', l)}>
          Merge duplicates
        </Button>);
    }
    if (buttons.length === 0 && row.group === 'review') {
      return <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>Decide in the review queue</Typography>;
    }
    return buttons;
  };

  return (
    <Box sx={LAYOUT} aria-label="Verity status checks">
      {/* 1. Status and arming */}
      <Section
        title="Verity status checks"
        area="status"
        action={(
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" startIcon={<HealthAndSafetyIcon />}
                    onClick={() => void runCheck()} disabled={healthBusy}
                    sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }}>
              Check Verity
            </Button>
            {canArm && (
              <Tooltip title={nightTip}>
                <span>
                  <Button variant="outlined" size="small"
                          startIcon={nightBusy ? <CircularProgress size={14} /> : <NightsStayIcon />}
                          onClick={openNight} disabled={!nightAllowed || nightBusy}
                          sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }}>
                    {nightBusy ? 'Running the nightly check…' : 'Run the nightly check now'}
                  </Button>
                </span>
              </Tooltip>
            )}
            {canArm && (
              <>
                <Button variant="outlined" size="small" startIcon={<ShieldIcon />}
                        onClick={() => setPending({ type: 'arm', scope: 'dry-run' })}
                        sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }}>
                  Arm dry run
                </Button>
                <Button variant="outlined" size="small" startIcon={<ShieldIcon />}
                        onClick={() => setPending({ type: 'arm', scope: 'enforce' })}
                        sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }}>
                  Arm enforce
                </Button>
              </>
            )}
            {arm && (
              <Button variant="outlined" size="small" color="error" startIcon={<PowerSettingsNewIcon />}
                      onClick={() => setPending({ type: 'disarm' })}>
                Disarm
              </Button>
            )}
          </Box>
        )}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {modeChip}
            {status.sendingState === 'paused' && <Chip label="Sending paused" color="warning" />}
            {status.killSwitch === true && (
              <Chip variant="outlined" label="SIMS_DISABLE_DRIP_SENDS is on" sx={{ color: 'var(--text-primary)' }} />
            )}
            {status.killSwitch === false && (
              <Chip variant="outlined" color="warning" label="SIMS_DISABLE_DRIP_SENDS is off" />
            )}
          </Box>
          <Line>
            {arm
              ? <>Armed for {scopeText(arm.scope)} by {arm.by || 'someone'} on {when(arm.at)}.{arm.reason ? ` Reason: “${arm.reason}”` : ''}</>
              : 'Not armed: SIMS reads no lead data from Verity.'}
          </Line>
          {status.killSwitch === true && (
            <Line muted>SIMS_DISABLE_DRIP_SENDS is on, so nothing can be emailed whatever the mode.</Line>
          )}
          <Line muted>
            {status.lastHealth
              ? <>Last Check Verity: {when(status.lastHealth.at)}{status.lastHealth.by ? ` by ${status.lastHealth.by}` : ''},{' '}
                  {status.lastHealth.ok ? 'passed.' : 'found problems.'}</>
              : 'Check Verity has not been run yet.'}
          </Line>
          {status.latch?.rejected && (
            <Alert severity="error">
              Verity rejected the login{status.latch.at ? ` on ${when(status.latch.at)}` : ''}. SIMS will not try that
              login again until a new version of the secret exists in Key Vault, so it cannot lock the account. Fix
              the value, add a new secret version, then Check Verity.
            </Alert>
          )}
          <Box>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600, mb: 0.5 }}>
              Before arming
            </Typography>
            {checks.map((c) => (
              <Box key={c.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.25 }}>
                {c.met ? <CheckCircleIcon fontSize="small" color="success" sx={{ mt: 0.25 }} />
                  : <RadioButtonUncheckedIcon fontSize="small" sx={{ mt: 0.25, color: 'var(--text-secondary)' }} />}
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere', minWidth: 0 }}>
                  {c.text} <em>({c.scope})</em>
                </Typography>
              </Box>
            ))}
          </Box>
          {canArm && <Line muted>{ARMING_TEXT}</Line>}
          {history.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600, mb: 0.5 }}>Recent changes</Typography>
              {history.map((h, i) => (
                <Line key={`${h.at}-${i}`} muted>
                  {when(h.at)}: {h.action === 'arm' ? `armed ${scopeText(h.scope)}`
                    : h.action === 'disarm' ? 'disarmed'
                      : h.action === 'run-check' ? 'ran the nightly check now' : String(h.action || '—')}
                  {' '}by {h.by || 'someone'}{h.reason ? ` — “${h.reason}”` : ''}
                </Line>
              ))}
            </Box>
          )}
        </Box>
      </Section>

      {/* 2. Last runs */}
      <Section title="Last runs" area="runs">
        {runs.length === 0 ? (
          <Line muted>No runs yet. The hourly sender records one each hour it runs in the send window once the checks are armed; the nightly check runs at 02:15 Pacific.</Line>
        ) : (
          <>
            <Line muted>In a dry run the counts are what SIMS would have done. Runs keep counts only, never a lead.</Line>
            <TableContainer sx={{ mt: 1, minWidth: 0 }}>
              <Table size="small" aria-label="Last Verity check runs">
                <TableHead>
                  <TableRow>
                    {['When', 'Run', 'Mode', 'Result', 'Counts', ''].map((h, i) => (
                      <TableCell key={h || `a-${i}`} sx={HEAD}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((run) => {
                    const result = runResult(run);
                    const undoable = canWrite && run.mode === 'enforce' && (stopTotal(run) > 0
                      || (run.resolutions || []).some((r) => r?.action === 'apply-pending'));
                    const label = `the ${runKindText(run.kind)} run of ${when(run.createdAt)}`;
                    return (
                      <TableRow key={run.id}>
                        <TableCell sx={{ ...CELL, ...NOWRAP }}>
                          <Tooltip title={`Run ${run.id}`}><span>{when(run.createdAt)}</span></Tooltip>
                        </TableCell>
                        <TableCell sx={{ ...CELL, ...NOWRAP }}>
                          {runKindText(run.kind)}
                          {run.trigger === 'manual' && (
                            <Typography variant="caption"
                                        sx={{ display: 'block', color: 'var(--text-secondary)', whiteSpace: 'normal' }}>
                              started by {run.by || 'someone'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...CELL, ...NOWRAP }}>{modeText(run.mode)}</TableCell>
                        <TableCell sx={CELL}>
                          <Chip size="small" label={result.label} color={result.color}
                                sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }} />
                        </TableCell>
                        <TableCell sx={CELL}>
                          <Box sx={COUNTS}>
                            {countItems(run).map((item) => <span key={item}>{item}</span>)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...CELL, ...NOWRAP }} align="right">
                          {undoable && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                              <Button size="small" onClick={() => setRunTarget({ runId: run.id, family: 'undo', action: 'preview-undo', label })}>
                                Preview undo
                              </Button>
                              <Button size="small" color="error" startIcon={<UndoIcon />}
                                      onClick={() => setRunTarget({ runId: run.id, family: 'undo', action: 'undo-stops', label })}>
                                Undo these stops
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Section>

      {/* 3. Review queue (the breaker) */}
      <Section title="Review queue" area="review">
        {pendingReview.length === 0 ? (
          <Line muted>Nothing waiting. When one run would stop more leads than the safety limit allows, it holds them here for a person to decide.</Line>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {pendingReview.map((p) => {
              const label = describeRun(p.runId);
              return (
                <Box key={p.runId} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                    <Line>{num(p.leads)} {num(p.leads) === 1 ? 'lead' : 'leads'} held for review by {label}.</Line>
                  </Box>
                  <Button size="small" onClick={() => setRunTarget({ runId: p.runId, family: 'review', action: 'preview-pending', label })}>
                    Preview
                  </Button>
                  <Button size="small" color="error"
                          onClick={() => setRunTarget({ runId: p.runId, family: 'review', action: 'apply-pending', label })}>
                    Apply stops
                  </Button>
                  <Button size="small"
                          onClick={() => setRunTarget({ runId: p.runId, family: 'review', action: 'release-pending', label })}>
                    Release
                  </Button>
                </Box>
              );
            })}
          </Box>
        )}
      </Section>

      {/* 4. Holds and stops */}
      <Section title="Holds and stops" area="holds">
        {leadsError && <Alert severity="error" sx={{ mb: 1 }}>{leadsError}</Alert>}
        {leadsLoading && leads.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
        ) : rows.length === 0 ? (
          !leadsError && <Line muted>No lead is held, stopped by Verity, waiting for review or not started.</Line>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip size="small" label={`All (${rows.length})`} onClick={() => setFilter('all')}
                    color={filter === 'all' ? 'primary' : 'default'} />
              {(Object.keys(GROUP_LABEL) as Group[]).filter((g) => groupCounts[g]).map((g) => (
                <Chip key={g} size="small" label={`${GROUP_LABEL[g]} (${groupCounts[g]})`}
                      onClick={() => setFilter(g)} color={filter === g ? 'primary' : 'default'} />
              ))}
            </Box>
            <TableContainer sx={{ minWidth: 0 }}>
              <Table size="small" aria-label="Leads held or stopped by the Verity check">
                <TableHead>
                  <TableRow>
                    {['Lead', 'What', 'Since', ''].map((h, i) => <TableCell key={h || `a-${i}`} sx={HEAD}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shownRows.slice(0, MAX_ROWS).map((row) => (
                    <TableRow key={`${row.group}-${row.lead.id}`}>
                      <TableCell sx={{ ...CELL, color: 'var(--text-primary)' }}>
                        {row.lead.firstName || '—'}
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', overflowWrap: 'break-word' }}>
                          {row.lead.email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={CELL}>
                        <Chip size="small" label={GROUP_LABEL[row.group]} color={GROUP_COLOR[row.group]}
                              variant="outlined" sx={{ mb: 0.5 }} />
                        <Box>{row.what}</Box>
                      </TableCell>
                      <TableCell sx={{ ...CELL, ...NOWRAP }}>
                        {when(row.since)}
                        {row.runId && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)' }}>
                            run {shortId(row.runId)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...CELL, ...NOWRAP }} align="right">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                          {rowActions(row)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {shownRows.length > MAX_ROWS && (
              <Line muted>Showing {MAX_ROWS} of {shownRows.length}. Verity Leads lists everyone.</Line>
            )}
          </>
        )}
      </Section>

      {/* 5. EAB cadence */}
      <Section
        title="EAB cadence"
        area="cadence"
        action={(
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canMarkWaiting && (
              <Tooltip title={simsCanSend
                ? 'SIMS is already sending, so it welcomes new leads itself. Mark a lead Admissions welcomed by hand on '
                  + 'its own, on Verity Leads.'
                : 'Go-live morning, once, right before you activate the emails'}>
                <span>
                  <Button variant="outlined" size="small" startIcon={<MarkEmailReadIcon />}
                          disabled={simsCanSend} onClick={() => void openBulk()}
                          sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }}>
                    Mark waiting leads as welcomed outside SIMS
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button variant="outlined" size="small" startIcon={<PlayArrowIcon />}
                    disabled={!cadence?.ready || activeCount > 0}
                    onClick={() => setPending({ type: 'cadence', action: 'activate' })}
                    sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }}>
              Activate cadence (all 7 emails)
            </Button>
            {activeCount > 0 && (
              <Button variant="outlined" size="small" color="warning" startIcon={<PauseIcon />}
                      onClick={() => setPending({ type: 'cadence', action: 'pause' })}>
                Pause cadence
              </Button>
            )}
          </Box>
        )}
      >
        {!cadence ? (
          <Line muted>The cadence could not be read.</Line>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Line><strong>{activeCount} of {required}</strong> emails active.</Line>
            {(cadence.missingDays || []).length > 0 && (
              <Line muted>
                No email for day {(cadence.missingDays || []).join(', ')}
                {(cadence.missingDays || []).includes(0) ? ' (day 0 must be the "When a lead is received" trigger)' : ''}.
                Load the EAB cadence, or add the missing entry.
              </Line>
            )}
            {(cadence.duplicateDays || []).length > 0 && (
              <Line muted>More than one email for day {(cadence.duplicateDays || []).join(', ')}: keep one of each.</Line>
            )}
            {(cadence.notRenderable || []).length > 0 && (
              <Line muted>These do not render completely yet: {(cadence.notRenderable || []).join(', ')}. Open them and fix the merge fields.</Line>
            )}
            {cadence.ready && activeCount > 0 && activeCount < required && (
              <Line muted>Some of the seven are active and some are not. Pause the cadence, then activate all seven together.</Line>
            )}
            {cadence.ready && activeCount === 0 && <Line muted>All seven are ready to switch on together.</Line>}
            {activeCount > 0 && cadence.activatedAt && (
              <Line muted>Activated by {cadence.activatedBy || 'someone'} on {when(cadence.activatedAt)}.</Line>
            )}
            <Line muted>The nurture emails (days 30 to 90) are never switched on or off here.</Line>
          </Box>
        )}
      </Section>

      {/* ── dialogs ──────────────────────────────────────────────────────────────────────── */}

      <Dialog open={healthOpen} onClose={healthBusy ? undefined : () => setHealthOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Check Verity</DialogTitle>
        <DialogContent dividers>
          {healthBusy && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 2 }}>
              <CircularProgress size={22} />
              <Typography variant="body2">Checking the Verity login and the status list. No lead data is read.</Typography>
            </Box>
          )}
          {healthError && <Alert severity="error">{healthError}</Alert>}
          {health && <HealthResult result={health} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHealthOpen(false)} disabled={healthBusy}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={nightOpen} onClose={nightBusy ? undefined : () => setNightOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{nightResult ? 'The nightly check, run now' : 'Run the nightly check now?'}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {!nightResult && <Typography variant="body2">{NIGHT_CHECK_TEXT}</Typography>}
          {!nightResult && !nightBusy && (
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
              SIMS checks every lead in Verity exactly as it does at 2:15 AM Pacific and records what it would
              do; no lead is emailed, stopped or held. One check runs at a time, and at most{' '}
              {NIGHT_CHECKS_PER_HOUR} an hour, for everyone together.
            </Typography>
          )}
          {nightBusy && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 1 }}>
              <CircularProgress size={22} />
              <Typography variant="body2">
                Running the nightly check. This takes up to 3 minutes: keep this page open. No email is sent.
              </Typography>
            </Box>
          )}
          {nightError && <Alert severity="error">{nightError}</Alert>}
          {nightResult && (
            <NightCheckResult result={nightResult} otherUnmet={nightOtherUnmet}
                              armedForEnforce={arm?.scope === 'enforce'} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNightOpen(false)} disabled={nightBusy}>{nightResult ? 'Close' : 'Cancel'}</Button>
          {!nightResult && (
            <Button variant="contained" disabled={nightBusy || !nightAllowed} onClick={() => void runNight()}>
              {nightBusy ? 'Running…' : 'Run the check now'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ActionDialog
        open={pending?.type === 'arm'}
        title={armScope === 'enforce' ? 'Arm enforce?' : 'Arm the dry run?'}
        typeToConfirm={armScope ? ARM_CONFIRM[armScope] : undefined}
        confirmLabel={armScope === 'enforce' ? 'Arm enforce' : 'Arm dry run'}
        danger={armScope === 'enforce'}
        blocked={armUnmet.length > 0}
        onClose={() => setPending(null)}
        onConfirm={async (reason, typed) => {
          if (!armScope) return;
          await verityControl({ action: 'arm', scope: armScope, confirm: typed, reason });
          setPending(null);
          done(armScope === 'enforce' ? 'Armed for enforce.' : 'Armed for the dry run.');
        }}
      >
        <Typography variant="body2">{ARMING_TEXT}</Typography>
        {armScope === 'dry-run' ? (
          <Typography variant="body2">
            In a dry run SIMS checks each lead in Verity and records what it would do. Nobody is emailed and no lead
            is stopped.
          </Typography>
        ) : (
          <Typography variant="body2">
            In enforce SIMS stops the leads Verity says to stop, holds the ones it cannot check, and emails the rest,
            each email checked against Verity no more than 15 minutes before it goes.
            {status.killSwitch !== false && ' SIMS_DISABLE_DRIP_SENDS is on, so SIMS keeps rehearsing until it is switched off.'}
          </Typography>
        )}
        {armUnmet.length > 0 && (
          <Alert severity="warning">
            Not yet:
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {armUnmet.map((t) => <li key={t}>{t}</li>)}
            </Box>
          </Alert>
        )}
      </ActionDialog>

      <ActionDialog
        open={pending?.type === 'disarm'}
        title="Disarm the Verity checks?"
        confirmLabel="Disarm"
        danger
        onClose={() => setPending(null)}
        onConfirm={async (reason) => {
          await verityControl({ action: 'disarm', reason });
          setPending(null);
          done('Disarmed. Nothing more is sent, and no lead is read from the next run.');
        }}
      >
        <Typography variant="body2">
          Disarm stops every email before its next send, even in a run already going, and stops lead reads from
          the next run. Nothing restarts on its own: arming again is a Survey.Admin action.
        </Typography>
      </ActionDialog>

      <ActionDialog
        open={pending?.type === 'cadence' && pending.action === 'activate'}
        title="Activate all seven EAB emails?"
        typeToConfirm={CADENCE_CONFIRM}
        confirmLabel="Activate cadence"
        blocked={!cadence?.ready}
        onClose={() => setPending(null)}
        onConfirm={async (reason, typed) => {
          await dripCadence({ action: 'activate', confirm: typed, reason });
          setPending(null);
          done('The seven EAB emails are active.');
        }}
      >
        <Typography variant="body2">
          All seven switch on together, or none do. Nobody is emailed while the Verity checks are in dry run or
          SIMS_DISABLE_DRIP_SENDS is on. A lead who has never been emailed starts with the Day 0 welcome, and its
          sequence counts from that first email - unless it is marked welcomed outside SIMS: then it starts at the
          next email. On go-live morning a Drip.Manager marks the waiting leads first (the button next to this one).
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          {(cadence?.names || []).map((n, i) => (
            <li key={`${n}-${i}`}><Typography variant="body2">Day {CADENCE_DAYS[i] ?? '?'}: {n || '(no name)'}</Typography></li>
          ))}
        </Box>
        {!cadence?.ready && <Alert severity="warning">The seven emails are not ready yet; see the EAB cadence card.</Alert>}
      </ActionDialog>

      <ActionDialog
        open={pending?.type === 'cadence' && pending.action === 'pause'}
        title="Pause the EAB cadence?"
        confirmLabel="Pause cadence"
        onClose={() => setPending(null)}
        onConfirm={async (reason) => {
          const r = await dripCadence({ action: 'pause', reason });
          setPending(null);
          done(`Paused ${(r?.entries || []).length} emails.`);
        }}
      >
        <Typography variant="body2">
          Pauses every active email of the seven. Nothing is due while they are paused. The nurture emails are not
          touched.
        </Typography>
      </ActionDialog>

      <ActionDialog
        open={leadPending?.action === 'resume'}
        title="Resume this lead?"
        confirmLabel="Resume"
        onClose={() => setLeadPending(null)}
        onConfirm={async (reason) => {
          if (!leadPending) return;
          await dripLeadAction(leadPending.lead.id, { action: 'resume', reason });
          setLeadPending(null);
          done('Resumed. Verity is checked again before any email.');
        }}
      >
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          <strong>{leadPending ? leadLabel(leadPending.lead, leadPending.lead) : ''}</strong>
          {leadPending?.lead.stoppedReason ? ` was stopped: ${leadPending.lead.stoppedReason}.` : ''}
        </Typography>
        <Typography variant="body2">{RESUME_TEXT}</Typography>
      </ActionDialog>

      <ActionDialog
        open={leadPending?.action === 'release-hold'}
        title="Release this hold?"
        confirmLabel="Release hold"
        blocked={releaseKind === 'not-found' && (retire.length === 0 || retiresAll)}
        onClose={() => setLeadPending(null)}
        onConfirm={async (reason) => {
          if (!leadPending) return;
          await dripLeadAction(leadPending.lead.id, {
            action: 'release-hold', reason, ...(releaseKind === 'not-found' ? { retireIds: retire } : {}),
          });
          setLeadPending(null);
          done('Hold released. Verity is checked again before any email.');
        }}
      >
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          <strong>{leadPending ? leadLabel(leadPending.lead, leadPending.lead) : ''}</strong>: {verityHoldText(releaseKind).long}
        </Typography>
        {releaseKind === 'possible-duplicate-verity-record' && (
          <Typography variant="body2">
            Release this once the records are merged in Verity. SIMS accepts the hand-offs so far; a new double
            hand-off holds the lead again. Verity is checked again before any email.
          </Typography>
        )}
        {releaseKind === 'not-found' && (
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Choose the ids Verity could not find because they were merged away in Verity. SIMS stops looking them
              up. At least one of the person’s ids must stay.
            </Typography>
            {notFoundIds.length === 0 && <Alert severity="info">This hold lists no ids.</Alert>}
            {notFoundIds.map((id) => (
              <FormControlLabel
                key={id}
                sx={{ display: 'flex', alignItems: 'flex-start' }}
                control={(
                  <Checkbox size="small" checked={retire.includes(id)} sx={{ pt: 0.25 }}
                            onChange={(e) => setRetire((cur) => (e.target.checked ? [...cur, id] : cur.filter((x) => x !== id)))} />
                )}
                label={<Typography variant="body2" sx={{ overflowWrap: 'anywhere', fontFamily: 'monospace' }}>{id}</Typography>}
              />
            ))}
            {retiresAll && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                That would retire every Verity id this person has. Leave at least one, or the lead can never be checked.
              </Alert>
            )}
          </Box>
        )}
      </ActionDialog>

      <ActionDialog
        open={leadPending?.action === 'merge-duplicates'}
        title="Merge these records?"
        confirmLabel="Merge duplicates"
        danger
        blocked={leadPerson.length < 2}
        onClose={() => setLeadPending(null)}
        onConfirm={async (reason) => {
          if (!leadPending) return;
          await dripLeadAction(leadPending.lead.id, { action: 'merge-duplicates', reason });
          setLeadPending(null);
          done('Merged. The person has one record now.');
        }}
      >
        <Typography variant="body2">
          These SIMS records share one address. The oldest is kept and takes the others’ history and Verity ids;
          the rest are marked merged and never emailed. A stop on any of them carries over. There is no automatic
          undo.
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {leadPerson.map((r) => (
            <li key={r.id}>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {leadLabel(r, r)} · {String(r.status || 'unknown')} · added {when(r.createdAt)}
                {survivor && r.id === survivor.id ? ' (kept)' : ''}
              </Typography>
            </li>
          ))}
        </Box>
        {leadPerson.length < 2 && <Alert severity="info">Only one record is listed for this address now. Reload the tab.</Alert>}
      </ActionDialog>

      <Dialog open={bulkOpen} onClose={bulkBusy ? undefined : () => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark waiting leads as welcomed outside SIMS?</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="warning">{WELCOMED_BULK_TEXT}</Alert>
          {bulkPreview?.blocked && <Alert severity="error">{bulkPreview.blocked}</Alert>}
          {!bulkPreview?.blocked && (
            <Alert severity="info">
              <strong>Before you confirm:</strong>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {WELCOMED_BULK_CHECKS.map((t) => <li key={t}>{t}</li>)}
              </Box>
            </Alert>
          )}
          {bulkBusy && !bulkPreview && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <CircularProgress size={22} />
              <Typography variant="body2">Counting the waiting leads. Nothing is changed yet.</Typography>
            </Box>
          )}
          {bulkPreview && (
            <>
              <Typography variant="body2">
                <strong>{num(bulkPreview.wouldMark)} waiting {num(bulkPreview.wouldMark) === 1 ? 'lead' : 'leads'}</strong>{' '}
                will be marked, each dated the day it arrived: every lead in sequence, arrived so far, that SIMS has
                never emailed. A lead that arrives after this count is left for SIMS's own welcome.
                {num(bulkPreview.alreadyMarked) > 0 && ` ${num(bulkPreview.alreadyMarked)} already marked stay as they are.`}
              </Typography>
              {welcomedSkipped(bulkPreview.skipped).length > 0 && (
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                  Left alone: {welcomedSkipped(bulkPreview.skipped).join(', ')}.
                  {num(bulkPreview.skipped?.held) > 0
                    && ' A lead on hold is not marked here: mark it on its own on Verity Leads if Admissions welcomed it.'}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                Each mark is recorded on the lead with your name, and can be undone on Verity Leads until SIMS emails
                that lead.
              </Typography>
            </>
          )}
          {bulkError && <Alert severity="error">{bulkError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} disabled={bulkBusy}>Cancel</Button>
          <Button variant="contained"
                  disabled={bulkBusy || !bulkPreview || !!bulkPreview.blocked || num(bulkPreview.wouldMark) === 0}
                  onClick={() => void runBulk()}>
            {bulkBusy && bulkPreview ? 'Marking…'
              : bulkPreview?.blocked ? 'Not after go-live'
                : bulkPreview && num(bulkPreview.wouldMark) === 0 ? 'No lead is waiting'
                  : `Mark ${num(bulkPreview?.wouldMark)} ${num(bulkPreview?.wouldMark) === 1 ? 'lead' : 'leads'}`}
          </Button>
        </DialogActions>
      </Dialog>

      <RunDialog
        target={runTarget}
        leadsById={leadsById}
        onClose={() => setRunTarget(null)}
        onDone={(message) => done(message)}
      />

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} message={toast ?? ''} />
    </Box>
  );
}
